// Launcher — headless API for server operations
// Engine-level: no DOM, no UI. Games build their own UI on top of this.

import type { ServerConfig, ServerInfo, MapInfo } from "../net/server.js";

export interface CreateServerResult {
  id: string;
  hostToken: string;
  wsUrl: string;
  server: ServerInfo;
}

/** Fetch list of active servers from a relay's lobby API. */
export async function listServers(relayHttp: string): Promise<ServerInfo[]> {
  const res = await fetch(`${relayHttp}/api/servers`);
  if (!res.ok) throw new Error(`Failed to list servers: ${res.status}`);
  const data = await res.json();
  return data.servers;
}

/** Create a new server on the relay. Returns server ID, host token, and WebSocket URL. */
export async function createServer(relayHttp: string, config: ServerConfig): Promise<CreateServerResult> {
  const res = await fetch(`${relayHttp}/api/servers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "unknown" }));
    throw new Error(err.error ?? `Failed to create server: ${res.status}`);
  }
  return res.json();
}

/** Close a server. Requires the host token returned from createServer. */
export async function closeServer(relayHttp: string, serverId: string, hostToken: string): Promise<void> {
  const res = await fetch(`${relayHttp}/api/servers/${serverId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${hostToken}` },
  });
  if (!res.ok) throw new Error(`Failed to close server: ${res.status}`);
}

/** Get info about a single server. */
export async function getServer(relayHttp: string, serverId: string): Promise<ServerInfo> {
  const res = await fetch(`${relayHttp}/api/servers/${serverId}`);
  if (!res.ok) throw new Error(`Server not found: ${res.status}`);
  const data = await res.json();
  return data.server;
}

/** Build the WebSocket URL for connecting to a server on a relay. */
export function serverWsUrl(relayWs: string, serverId: string): string {
  return `${relayWs}/server/${serverId}`;
}

// --- Community maps ---

export interface PublishMapResult {
  code: string;
  creatorToken: string;
  map: MapInfo;
}

/** List community maps from the relay. */
export async function listMaps(relayHttp: string, sort = "popular", mode?: string, limit = 50): Promise<MapInfo[]> {
  const params = new URLSearchParams({ sort, limit: String(limit) });
  if (mode) params.set("mode", mode);
  const res = await fetch(`${relayHttp}/api/maps?${params}`);
  if (!res.ok) throw new Error(`Failed to list maps: ${res.status}`);
  const data = await res.json();
  return data.maps;
}

/** Get info about a single community map. */
export async function getMap(relayHttp: string, code: string): Promise<MapInfo> {
  const res = await fetch(`${relayHttp}/api/maps/${code}`);
  if (!res.ok) throw new Error(`Map not found: ${res.status}`);
  const data = await res.json();
  return data.map;
}

/** Publish a map to the community. */
export async function publishMap(relayHttp: string, data: {
  name: string; description?: string;
  creatorName?: string; scene: string; thumbnail?: string;
}): Promise<PublishMapResult> {
  const res = await fetch(`${relayHttp}/api/maps`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "unknown" }));
    throw new Error(err.error ?? `Failed to publish: ${res.status}`);
  }
  return res.json();
}

/** Update a published map. Requires the creator token. */
export async function updateMap(relayHttp: string, code: string, creatorToken: string, data: {
  name?: string; description?: string; scene?: string; thumbnail?: string;
}): Promise<void> {
  const res = await fetch(`${relayHttp}/api/maps/${code}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${creatorToken}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update map: ${res.status}`);
}

/** Delete a published map. Requires the creator token. */
export async function deleteMap(relayHttp: string, code: string, creatorToken: string): Promise<void> {
  const res = await fetch(`${relayHttp}/api/maps/${code}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${creatorToken}` },
  });
  if (!res.ok) throw new Error(`Failed to delete map: ${res.status}`);
}

/** Vote on a community map. Quality (up/down) and difficulty (0=easy, 1=medium, 2=hard) are both optional. */
export async function voteMap(relayHttp: string, code: string, up?: boolean, difficulty?: number): Promise<void> {
  const body: Record<string, unknown> = {};
  if (up !== undefined) body.up = up;
  if (difficulty !== undefined) body.difficulty = difficulty;
  const res = await fetch(`${relayHttp}/api/maps/${code}/vote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to vote: ${res.status}`);
}
